import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import RealTimePrice from '@/components/shared/real-time-price/RealTimePrice';
import { formatInstrumentPrice } from '@/utils/formatters';
import { useCrmCalculateTrade } from '@/api/instruments/use-crm-calculate';
import { useCrmAllPrices } from '@/hooks/useCrmAllPrices';
import { Trade } from '@/models/trade';

interface EditStopLossTakeProfitDialogProps {
    trade: Trade | null;
    type: 'stop_loss' | 'take_profit';
    open: boolean;
    onClose: () => void;
    onSave: (data: { stop_loss_price?: number; take_profit_price?: number }) => Promise<void>;
}

export const EditStopLossTakeProfitDialog: React.FC<EditStopLossTakeProfitDialogProps> = ({
    trade,
    type,
    open,
    onClose,
    onSave,
}) => {
    const [mode, setMode] = useState<'price' | 'pnl'>('price');
    const [priceValue, setPriceValue] = useState('');
    const [pnlValue, setPnlValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const prevPnlRef = useRef<string | null>(null);

    const calculateMutation = useCrmCalculateTrade();
    const { prices } = useCrmAllPrices(trade?.instrument_id ? [trade.instrument_id] : []);

    const instrument = trade?.instrument;
    const instrumentId = trade?.instrument_id;
    const side = trade?.side || 'buy';
    const lotSize = trade?.lot_size ?? 0;
    const entryPrice = trade?.entry_price ?? 0;
    const leverage = trade?.leverage || 100;

    const currentValue = type === 'stop_loss' ? trade?.stop_loss_price : trade?.take_profit_price;
    const derivedTargetPrice = calculateMutation.data?.calculations?.derived_target_price;
    const isCalculating = calculateMutation.isPending;

    const currentPriceData = instrumentId ? prices?.get?.(instrumentId) : null;
    const currentPrice =
        currentPriceData?.last ?? currentPriceData?.bid ?? currentPriceData?.ask ?? 0;

    // Fetch when pnlValue changes (no timeout, throttle only)
    useEffect(() => {
        if (
            !open ||
            !trade ||
            mode !== 'pnl' ||
            !instrumentId ||
            !pnlValue ||
            !entryPrice ||
            !lotSize
        )
            return;

        const pnl = parseFloat(pnlValue);
        if (isNaN(pnl)) return;

        prevPnlRef.current = pnlValue;

        calculateMutation.mutate({
            instrument_id: instrumentId,
            lot_size: lotSize,
            leverage,
            entry_price: entryPrice,
            target_pnl: pnl,
            side: side as 'buy' | 'sell',
        });
    }, [mode, pnlValue, priceValue, currentPrice]);

    useEffect(() => {
        if (open && trade) {
            setPriceValue(currentValue ? String(currentValue) : '');
            setPnlValue('');
            setMode('price');
            prevPnlRef.current = null;
            calculateMutation.reset();
        }
    }, [open, trade?.id, type]);

    const handleSave = async () => {
        if (!trade) return;
        let targetPrice: number;
        if (mode === 'price') {
            const p = parseFloat(priceValue);
            if (isNaN(p) || p <= 0) return;
            targetPrice = p;
        } else {
            if (derivedTargetPrice === undefined || derivedTargetPrice === null) return;
            targetPrice = derivedTargetPrice;
        }
        setIsSaving(true);
        try {
            if (type === 'stop_loss') {
                await onSave({ stop_loss_price: targetPrice });
            } else {
                await onSave({ take_profit_price: targetPrice });
            }
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const title = type === 'stop_loss' ? 'Edit Stop Loss' : 'Edit Take Profit';
    const label = type === 'stop_loss' ? 'Stop Loss' : 'Take Profit';

    if (!trade) return null;

    return (
        <Dialog open={open} onOpenChange={open => !open && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {title}
                        {instrument?.symbol && <Badge variant="outline">{instrument.symbol}</Badge>}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div>
                        <Label className="text-sm text-muted-foreground">Entry Price</Label>
                        <div className="mt-1 font-mono font-medium">
                            {entryPrice > 0
                                ? formatInstrumentPrice(entryPrice, instrument as any)
                                : '-'}
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm text-muted-foreground">Current Price</Label>
                        <div className="mt-1 font-mono">
                            {instrument ? (
                                <RealTimePrice
                                    instrument={instrument as any}
                                    fallbackPrice={0}
                                    className="font-mono"
                                />
                            ) : (
                                '-'
                            )}
                        </div>
                    </div>
                    <div>
                        <Label className="mb-2 block">Mode</Label>
                        <RadioGroup
                            value={mode}
                            onValueChange={v => setMode(v as 'price' | 'pnl')}
                            className="flex gap-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="price" id="mode-price" />
                                <Label htmlFor="mode-price" className="font-normal cursor-pointer">
                                    Target Price
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="pnl" id="mode-pnl" />
                                <Label htmlFor="mode-pnl" className="font-normal cursor-pointer">
                                    Target P&L
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>
                    {mode === 'price' ? (
                        <div>
                            <Label htmlFor="target-price">{label} Price</Label>
                            <div className="mt-1 flex gap-2">
                                <Input
                                    id="target-price"
                                    type="number"
                                    step="0.00001"
                                    min="0"
                                    value={priceValue}
                                    onChange={e => setPriceValue(e.target.value)}
                                    placeholder="0.00000"
                                    className="font-mono flex-1"
                                />
                                {instrument && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            if (currentPrice > 0) {
                                                setPriceValue(String(currentPrice));
                                            }
                                        }}
                                        disabled={!currentPrice || currentPrice <= 0}
                                        title="Use current price"
                                    >
                                        Current
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Label htmlFor="target-pnl">Target P&L ($)</Label>
                            <Input
                                id="target-pnl"
                                type="number"
                                step="0.01"
                                value={pnlValue}
                                onChange={e => setPnlValue(e.target.value)}
                                placeholder={type === 'stop_loss' ? 'e.g. -100' : 'e.g. 100'}
                                className="font-mono"
                            />
                            {pnlValue && (
                                <div className="rounded-lg border bg-muted/30 p-2">
                                    <div className="text-xs text-muted-foreground">
                                        Derived {label} Price
                                    </div>
                                    <div className="mt-1 font-mono font-medium">
                                        {isCalculating
                                            ? 'Calculating...'
                                            : derivedTargetPrice !== undefined &&
                                                derivedTargetPrice !== null
                                              ? formatInstrumentPrice(
                                                    derivedTargetPrice,
                                                    instrument as any
                                                )
                                              : '-'}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            isSaving ||
                            (mode === 'price' && (!priceValue || parseFloat(priceValue) <= 0)) ||
                            (mode === 'pnl' &&
                                (!pnlValue ||
                                    derivedTargetPrice === undefined ||
                                    derivedTargetPrice === null))
                        }
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
